import React, { useState, useRef } from 'react';
import { ALL_REPORT_TYPES, REPORT_TYPE_LABELS } from '../constants/reportTypes';
import { ProposalTemplateSummary } from './TemplateLibraryView';

interface TemplateUploadDialogProps {
  token: string | null;
  /** If provided, edits an existing template (rename + retag). File replacement also allowed. */
  template?: ProposalTemplateSummary;
  onClose: () => void;
  onUploaded: () => void;
}

const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

export const TemplateUploadDialog: React.FC<TemplateUploadDialogProps> = ({
  token,
  template,
  onClose,
  onUploaded,
}) => {
  const isEdit = !!template;
  const [title, setTitle] = useState(template?.title || '');
  const [description, setDescription] = useState(template?.description || '');
  const [reportTypes, setReportTypes] = useState<string[]>(template?.reportTypes || []);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleReportType = (type: string) => {
    setReportTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleFileSelect = (f: File) => {
    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!allowed.includes(f.type) && !f.name.match(/\.(pdf|docx?|xlsx?)$/i)) {
      setError('Only PDF, DOCX, and XLSX files are allowed');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setError('File size must be under 20MB');
      return;
    }
    setFile(f);
    setError(null);
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!isEdit && !file) {
      setError('Please select a file');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && !file) {
        // Metadata-only update
        const res = await fetch(`${apiUrl}/api/proposals/templates/${template!.id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: title.trim(),
            description: description.trim() || null,
            reportTypes,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || 'Failed to update template');
          return;
        }
        onUploaded();
        return;
      }

      // File upload (new or replace)
      const formData = new FormData();
      formData.append('template', file!);
      formData.append('title', title.trim());
      if (description.trim()) formData.append('description', description.trim());
      formData.append('reportTypes', JSON.stringify(reportTypes));

      const res = await fetch(`${apiUrl}/api/proposals/templates/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to upload template');
        return;
      }
      onUploaded();
    } catch {
      setError('Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-dark-surface rounded-lg shadow-xl border border-gray-200 dark:border-dark-border w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Template' : 'Upload Template'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Template Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              placeholder="e.g., Commercial Property Renewal Proposal"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              placeholder="Brief description of when to use this template..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Applicable Report Types
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Select every report type this template can be used with. Leave all unchecked to make it available for any report type.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-3">
              {ALL_REPORT_TYPES.map((type) => (
                <label
                  key={type}
                  className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={reportTypes.includes(type)}
                    onChange={() => toggleReportType(type)}
                    className="rounded"
                  />
                  {REPORT_TYPE_LABELS[type] || type}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isEdit ? 'Replace File (optional)' : 'Template File (PDF, DOCX, or XLSX) *'}
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300 dark:border-gray-600 hover:border-primary'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files[0];
                if (f) handleFileSelect(f);
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? (
                <div className="text-sm">
                  <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              ) : isEdit ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Current file: <span className="font-medium">{template!.originalFileName}</span>
                  <br />
                  Click or drag to replace.
                </p>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Drag & drop your template here, or click to browse.
                  <br />
                  <span className="text-xs text-gray-400">PDF, DOCX, or XLSX (max 20MB)</span>
                </p>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileSelect(f);
              }}
            />
          </div>

          {isEdit && template?.isSystemDefault && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-800 dark:text-amber-300 text-sm">
              Editing this template will remove its "Default" status. A fresh default will be re-created automatically the next time a user lists templates for this report type, unless you keep this one.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Upload Template'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateUploadDialog;
